import { gql } from '@apollo/client';

// DD-3697 Price requests & price history. Field lists mirror the backend's
// price-request types.graphql; the page components pick what they render.
const priceValuesFields = `
    price
    tiotandvardPrice
    deliveryTime
`;

const priceRequestFields = `
    _id
    number
    chain { _id name }
    lab { _id name }
    actionSet
    actionSetTitle
    currency
    initiatorSide
    status
    note
    revision
    summary { changed unchanged withdrawn }
    createdByName
    createdAt
    updatedAt
    viewerSide
    allowedActions
`;

const priceRequestDetailFields = `
    ${priceRequestFields}
    lines {
        _id
        action
        category
        actionNumber
        actionName
        current { ${priceValuesFields} }
        currentEnabled
        outcome
        proposed { ${priceValuesFields} }
        priceDelta
        pricePercent
    }
    statusLog { status changedAt changedByName changedBySide }
    categoryRanks { category rank }
    availableProducts {
        action
        actionNumber
        actionName
        category
        categoryName
        current { ${priceValuesFields} }
        currentEnabled
        hasCategoryPriceList
    }
`;

const priceRequestMessageFields = `
    _id
    text
    senderName
    senderSide
    createdAt
    isOwn
`;

export const priceRequestsTemplate = gql`
    query priceRequests(
        $counterpartId: MongoDbObjectId
        $statuses: [PriceRequestStatus!]
        $limit: Int
        $offset: Int
    ) {
        priceRequests(counterpartId: $counterpartId, statuses: $statuses, limit: $limit, offset: $offset) {
            total
            items { ${priceRequestFields} }
        }
    }
`;

export const priceRequestCounterpartsTemplate = gql`
    query priceRequestCounterparts {
        priceRequestCounterparts {
            _id
            name
            currency
            activeActionSet
        }
    }
`;

export const priceRequestTemplate = gql`
    query priceRequest($id: MongoDbObjectId!) {
        priceRequest(id: $id) { ${priceRequestDetailFields} }
    }
`;

export const priceRequestMessagesTemplate = gql`
    query priceRequestMessages($priceRequestId: MongoDbObjectId!) {
        priceRequestMessages(priceRequestId: $priceRequestId) { ${priceRequestMessageFields} }
    }
`;

export const productPriceHistoryTemplate = gql`
    query productPriceHistory(
        $counterpartId: MongoDbObjectId!
        $actionId: MongoDbObjectId!
        $actionSetId: MongoDbObjectId
    ) {
        productPriceHistory(counterpartId: $counterpartId, actionId: $actionId, actionSetId: $actionSetId) {
            action
            actionNumber
            actionName
            chain { _id name }
            lab { _id name }
            actionSet
            currency
            current { ${priceValuesFields} }
            isWithdrawn
            entries {
                _id
                type
                source
                values { ${priceValuesFields} }
                previous { ${priceValuesFields} }
                previousEnabled
                priceDelta
                pricePercent
                currency
                priceRequest
                priceRequestNumber
                changedByName
                effectiveAt
            }
        }
    }
`;

export const createPriceRequestTemplate = gql`
    mutation createPriceRequest($input: CreatePriceRequestInput!) {
        createPriceRequest(input: $input) { ${priceRequestDetailFields} }
    }
`;

export const updatePriceRequestDraftTemplate = gql`
    mutation updatePriceRequestDraft(
        $id: MongoDbObjectId!
        $note: String
        $actionIds: [MongoDbObjectId!]
        $categoryRanks: [PriceRequestCategoryRankInput!]
    ) {
        updatePriceRequestDraft(id: $id, note: $note, actionIds: $actionIds, categoryRanks: $categoryRanks) {
            ${priceRequestDetailFields}
        }
    }
`;

export const savePriceRequestLinesTemplate = gql`
    mutation savePriceRequestLines($id: MongoDbObjectId!, $lines: [PriceRequestLineInput!]!) {
        savePriceRequestLines(id: $id, lines: $lines) { ${priceRequestDetailFields} }
    }
`;

export const sendPriceRequestTemplate = gql`
    mutation sendPriceRequest($id: MongoDbObjectId!) {
        sendPriceRequest(id: $id) { ${priceRequestDetailFields} }
    }
`;

export const respondToPriceRequestTemplate = gql`
    mutation respondToPriceRequest($id: MongoDbObjectId!, $lines: [PriceRequestLineInput!]) {
        respondToPriceRequest(id: $id, lines: $lines) { ${priceRequestDetailFields} }
    }
`;

export const approvePriceRequestTemplate = gql`
    mutation approvePriceRequest($id: MongoDbObjectId!, $revision: Int!) {
        approvePriceRequest(id: $id, revision: $revision) { ${priceRequestDetailFields} }
    }
`;

export const rejectPriceRequestTemplate = gql`
    mutation rejectPriceRequest($id: MongoDbObjectId!) {
        rejectPriceRequest(id: $id) { ${priceRequestDetailFields} }
    }
`;

export const cancelPriceRequestTemplate = gql`
    mutation cancelPriceRequest($id: MongoDbObjectId!) {
        cancelPriceRequest(id: $id) { ${priceRequestDetailFields} }
    }
`;

export const postPriceRequestMessageTemplate = gql`
    mutation postPriceRequestMessage($priceRequestId: MongoDbObjectId!, $text: String!) {
        postPriceRequestMessage(priceRequestId: $priceRequestId, text: $text) { ${priceRequestMessageFields} }
    }
`;
